// src/components/modals/CreateAdoptionPostModal.jsx
"use client";

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { resolveImageUrl } from '../../utils/imageUtils';
import { getUserTypeColor } from '../../utils/userTypeUtils';
import { getPetName, getSpeciesName, getBreedName } from '../../utils/petDataUtils';
import { useAuth } from '../../hooks/useAuth';
import './CreateAdoptionPostModal.css';

const CreateAdoptionPostModal = ({ 
  isOpen, 
  onClose, 
  selectedPets,
  editingPost = null,
  onSubmit 
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const { getUserType } = useAuth();
  
  const [aboutPet, setAboutPet] = useState('');
  const [lookingFor, setLookingFor] = useState('');
  const [additionalImages, setAdditionalImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [vaccinated, setVaccinated] = useState(null); // null | boolean
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const scrollLockRef = useRef(0);

  // Get user type color
  const userType = getUserType();
  const userTypeColor = getUserTypeColor(userType);

  // Reset form when modal opens/closes or load edit data
  useEffect(() => {
    if (!isOpen) {
      setAboutPet('');
      setLookingFor('');
      setAdditionalImages([]);
      setImagePreviews([]);
      setRemovedImageIds([]);
      setError(null);
      setVaccinated(null);
    } else if (editingPost) {
      // Load editing post data
      setAboutPet(editingPost.about_pet || '');
      setLookingFor(editingPost.looking_for || '');
      // Load vaccinated flag from existing post if present
      if (Object.prototype.hasOwnProperty.call(editingPost, 'vaccinated')) {
        setVaccinated(
          editingPost.vaccinated === null || editingPost.vaccinated === undefined
            ? null
            : Boolean(editingPost.vaccinated)
        );
      } else if (
        Object.prototype.hasOwnProperty.call(editingPost, 'is_vaccinated')
      ) {
        setVaccinated(
          editingPost.is_vaccinated === null || editingPost.is_vaccinated === undefined
            ? null
            : Boolean(editingPost.is_vaccinated)
        );
      } else {
        setVaccinated(null);
      }
      // Handle existing images if any
      if (editingPost.additional_images && editingPost.additional_images.length > 0) {
        const existingImages = editingPost.additional_images.map(img => {
          if (typeof img === 'string') {
            return { url: img, isExisting: true };
          }
          return { 
            url: img.url || img.path, 
            id: img.id,
            isExisting: true 
          };
        });
        setImagePreviews(existingImages);
      }
    }
  }, [isOpen, editingPost]);

  // Handle keyboard events and body scroll without losing page scroll position
  useEffect(() => {
    if (!isOpen) return;

    scrollLockRef.current = window.scrollY || window.pageYOffset || 0;

    const originalStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLockRef.current}px`;
    document.body.style.width = '100%';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalStyle.overflow;
      document.body.style.position = originalStyle.position;
      document.body.style.top = originalStyle.top;
      document.body.style.width = originalStyle.width;
      window.scrollTo({ top: scrollLockRef.current || 0, behavior: "instant" });
    };
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isSubmitting) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (validFiles.length === 0) {
      setError(t('adoption.modals.createPost.invalidImage') || 'Please select valid image files');
      return;
    }

    // Limit to 5 total images (including existing ones when editing)
    // Use imagePreviews.length to account for both existing and new images
    const remainingSlots = 5 - imagePreviews.length;
    
    if (remainingSlots <= 0) {
      setError(t('adoption.modals.createPost.maxImagesReached') || 'Maximum of 5 images allowed');
      return;
    }
    
    const filesToAdd = validFiles.slice(0, remainingSlots);
    
    // Clear any previous errors
    setError(null);
    
    // Add new files to additionalImages array (for upload)
    setAdditionalImages(prev => [...prev, ...filesToAdd]);
    
    // Create previews for new files
    filesToAdd.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, {
          file,
          preview: reader.result,
          isExisting: false
        }]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    const preview = imagePreviews[index];
    // If it's an existing image (has id), track it for removal
    if (preview && preview.id && preview.isExisting) {
      setRemovedImageIds(prev => [...prev, preview.id]);
    }
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!aboutPet.trim()) {
      setError(t('adoption.modals.createPost.aboutRequired') || 'Please tell us about the pet(s)');
      return;
    }

    if (!lookingFor.trim()) {
      setError(t('adoption.modals.createPost.lookingForRequired') || 'Please describe what you\'re looking for in an adopter');
      return;
    }

    if (vaccinated === null) {
      setError(
        t('adoption.modals.createPost.vaccinatedRequired') ||
          'Please specify whether the pet is vaccinated'
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const postData = {
        pet_ids: Array.isArray(selectedPets) 
          ? selectedPets.map(pet => typeof pet === 'object' ? pet.id : pet)
          : [selectedPets],
        about_pet: aboutPet.trim(),
        looking_for: lookingFor.trim(),
        additional_images: additionalImages,
        vaccinated
      };

      // If editing, include removed image IDs
      if (editingPost && removedImageIds.length > 0) {
        postData.removed_image_ids = removedImageIds;
      }

      await onSubmit(postData);
      onClose();
    } catch (err) {
      setError(err.message || t('adoption.modals.createPost.submitError') || 'Failed to create adoption post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPetImage = (pet) => {
    if (!pet) return "/images/no-image-pet.png";

    // First, try direct fields that are commonly used in adoption posts
    // This matches the logic in AdoptionPost.jsx
    if (pet.picture) {
      const resolved = resolveImageUrl(pet.picture);
      if (resolved) return resolved;
    }
    
    if (pet.profile_image_url) {
      const resolved = resolveImageUrl(pet.profile_image_url);
      if (resolved) return resolved;
    }

    // Handle profile_image_path which might be a string or an object { url, path, ... }
    const profilePath =
      typeof pet.profile_image_path === "string"
        ? pet.profile_image_path
        : pet.profile_image_path?.url || pet.profile_image_path?.path;

    // Try to resolve image from multiple possible fields
    // Check in order: adoption post fields first, then other common fields
    const imageUrl = resolveImageUrl(
      // Fields from adoption post data (most common)
      pet.image,
      // Transformed field from usePets (primaryImageUrl)
      pet.primaryImageUrl,
      // Raw backend-style fields
      pet.primary_image_url,
      profilePath,
      pet.avatar,
      pet.profileImageUrl,
      pet.photo,
      pet.pictures?.[0]?.url,
      pet.pictures?.[0]?.path,
      pet.media?.[0]?.url,
      pet.media?.[0]?.path,
      pet.gallery_images?.[0]?.url,
      pet.gallery_images?.[0]?.path
    );

    return imageUrl || "/images/no-image-pet.png";
  };


  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className="create-adoption-post-modal-overlay"
      onClick={handleBackdropClick}
      style={{ '--adoption-accent': userTypeColor }}
    >
      <div 
        className="create-adoption-post-modal"
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="create-adoption-post-modal-header">
          <h2 className="create-adoption-post-modal-title">
            {editingPost 
              ? (t('adoption.modals.createPost.editTitle') || 'Edit Adoption Post')
              : (t('adoption.modals.createPost.title') || 'Create Adoption Post')}
          </h2>
          <button
            className="create-adoption-post-modal-close"
            onClick={onClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="create-adoption-post-modal-content">
          {/* Selected Pets Display */}
          <div className="create-adoption-post-pets-section">
            <h3 className="create-adoption-post-section-title">
              {t('adoption.modals.createPost.selectedPets') || 'Selected Pet(s) for Adoption'}
            </h3>
            <div className="create-adoption-post-pets-list">
              {selectedPets.map(pet => {
                const petImage = getPetImage(pet);
                const petName = getPetName(pet) || t('adoption.modals.createPost.unknownPet') || 'Unknown Pet';
                const speciesName = getSpeciesName(pet.species) || t('adoption.modals.createPost.unknownSpecies') || 'Unknown';
                const breedName = getBreedName(pet.breed) || t('adoption.modals.createPost.unknownBreed') || 'Unknown';
                
                return (
                  <div key={pet.id} className="create-adoption-post-pet-card">
                    <img
                      src={petImage}
                      alt={petName}
                      onError={(e) => {
                        e.target.src = "/images/no-image-pet.png";
                      }}
                    />
                    <div className="create-adoption-post-pet-info">
                      <h4>{petName}</h4>
                      <p>
                        {speciesName} • {breedName}
                        {pet.gender && ` • ${pet.gender}`}
                        {pet.color && ` • ${pet.color}`}
                        {(pet.calculated_age || pet.age_formatted || pet.age) 
                          ? ` • ${pet.calculated_age || pet.age_formatted || pet.age}` 
                          : (pet.birth_date && ` • ${new Date(pet.birth_date).toLocaleDateString()}`)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Two-column layout: text fields + images */}
          <div className="create-adoption-post-body-grid">
            <div className="create-adoption-post-body-main">
              {/* About Pet Section */}
              <div className="create-adoption-post-section">
                <label className="create-adoption-post-label">
                  {t('adoption.modals.createPost.aboutPet') || 'Tell us about the pet(s)'}
                  <span className="required">*</span>
                </label>
                <textarea
                  className="create-adoption-post-textarea"
                  value={aboutPet}
                  onChange={(e) => setAboutPet(e.target.value)}
                  placeholder={t('adoption.modals.createPost.aboutPetPlaceholder') || 'Describe the pet(s), their personality, health status, special needs, etc.'}
                  rows={5}
                  disabled={isSubmitting}
                />
              </div>

              {/* Looking For Section */}
              <div className="create-adoption-post-section">
                <label className="create-adoption-post-label">
                  {t('adoption.modals.createPost.lookingFor') || 'What are you looking for in an adopter?'}
                  <span className="required">*</span>
                </label>
                <textarea
                  className="create-adoption-post-textarea"
                  value={lookingFor}
                  onChange={(e) => setLookingFor(e.target.value)}
                  placeholder={t('adoption.modals.createPost.lookingForPlaceholder') || 'Describe the ideal adopter, requirements, expectations, etc.'}
                  rows={5}
                  disabled={isSubmitting}
                />
              </div>

              {/* Vaccinated toggle */}
              <div className="create-adoption-post-section">
                <label className="create-adoption-post-label">
                  {t('adoption.modals.createPost.vaccinatedLabel') || 'Is the pet vaccinated?'}
                </label>
                <div className="create-adoption-post-toggle-group">
                  <button
                    type="button"
                    className={`create-adoption-post-toggle-btn ${
                      vaccinated === true ? 'is-active-yes' : ''
                    }`}
                    onClick={() => setVaccinated(true)}
                    disabled={isSubmitting}
                  >
                    {t('common.yes') || 'Yes'}
                  </button>
                  <button
                    type="button"
                    className={`create-adoption-post-toggle-btn ${
                      vaccinated === false ? 'is-active-no' : ''
                    }`}
                    onClick={() => setVaccinated(false)}
                    disabled={isSubmitting}
                  >
                    {t('common.no') || 'No'}
                  </button>
                </div>
                <p className="create-adoption-post-hint">
                  {t('adoption.modals.createPost.vaccinatedHint') ||
                    'This will be shown on the adoption details to help adopters understand the pet’s health status.'}
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="create-adoption-post-error">
                  {error}
                </div>
              )}
            </div>

            {/* Additional Images Section (side column) */}
            <aside className="create-adoption-post-body-aside">
              <div className="create-adoption-post-section create-adoption-post-images-section">
                <label className="create-adoption-post-label">
                  {t('adoption.modals.createPost.additionalImages') || 'Additional Pet Pictures (Optional)'}
                </label>
                <p className="create-adoption-post-hint">
                  {t('adoption.modals.createPost.additionalImagesHint') || 'You can add up to 5 additional pictures'}
                </p>
                
                {imagePreviews.length > 0 && (
                  <div className="create-adoption-post-images-preview">
                    {imagePreviews.map((item, index) => (
                      <div key={index} className="create-adoption-post-image-preview">
                        <img 
                          src={item.preview || item.url} 
                          alt={`Preview ${index + 1}`} 
                          onError={(e) => {
                            e.target.src = "/images/no-image-pet.png";
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="create-adoption-post-remove-image"
                          disabled={isSubmitting}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {imagePreviews.length < 5 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="create-adoption-post-add-image-btn"
                    disabled={isSubmitting}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {t('adoption.modals.createPost.addImage') || 'Add Image'}
                  </button>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                  disabled={isSubmitting}
                />
              </div>
            </aside>
          </div>
        </div>

        {/* Footer */}
        <div className="create-adoption-post-modal-footer">
          <button
            className="create-adoption-post-modal-cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            className="create-adoption-post-modal-submit"
            onClick={handleSubmit}
            disabled={isSubmitting || !aboutPet.trim() || !lookingFor.trim()}
          >
            {isSubmitting ? (
              <>
                <div className="create-adoption-post-spinner"></div>
                {editingPost 
                  ? (t('adoption.modals.createPost.updating') || 'Updating...')
                  : (t('adoption.modals.createPost.posting') || 'Posting...')}
              </>
            ) : (
              editingPost 
                ? (t('adoption.modals.createPost.update') || 'Update Post')
                : (t('adoption.modals.createPost.post') || 'Post')
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CreateAdoptionPostModal;

